import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { submitHireRequest, fetchTools } from "services/network/talent";
import { analytics } from "services/analytics";
import LANGUAGES from "constants/language";

import { type FormData, INITIAL_FORM, STEP_LABELS, EXPERIENCE_LEVELS, PERSONALITY_QUESTIONS } from "./types";
import Topbar from "./Topbar";
import IntroCard from "./IntroCard";
import StepBasics from "./StepBasics";
import StepCapabilities from "./StepCapabilities";
import StepPersonality from "./StepPersonality";
import StepGoodToKnow from "./StepGoodToKnow";
import StepReview from "./StepReview";
import SuccessCard from "./SuccessCard";

import "./RequestAHirePage.scss";

const STEP_NAMES = ["basics", "capabilities", "personality", "good_to_know", "review"] as const;

// ─── Main Page ────────────────────────────────────────────────────────────────

function RequestAHirePage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"intro" | "form">("intro");
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState<FormData | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [toolOptions, setToolOptions] = useState<string[]>([]);

  // Hide the app sidebar and main header while this journey is active
  useEffect(() => {
    document.body.classList.add("rah-journey-active");
    return () => document.body.classList.remove("rah-journey-active");
  }, []);

  // Fetch tool options for Step 2 autocomplete
  useEffect(() => {
    fetchTools()
      .then((tools) =>
        setToolOptions(
          tools
            .filter((t) => t.active !== false)
            .map((t) => t.name)
            .filter((name, i, arr) => arr.indexOf(name) === i)
            .sort((a, b) => a.localeCompare(b))
        )
      )
      .catch(() => setToolOptions([]));
  }, []);

  const handleChange = (patch: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const handleBack = () => {
    if (submitted) {
      navigate(-1);
    } else if (phase === "intro") {
      navigate(-1);
    } else if (phase === "form" && currentStep === 0) {
      setPhase("intro");
    } else {
      setCurrentStep((s) => Math.max(s - 1, 0));
    }
  };

  const handleStart = () => {
    analytics.track("Hire Request Started", { context: "intro" });
    setPhase("form");
    setCurrentStep(0);
  };

  const handleContinue = () => {
    analytics.track("Hire Request Step Completed", {
      stepName: STEP_NAMES[currentStep] ?? `step_${currentStep}`,
      stepIndex: currentStep,
    });
    setCurrentStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  };

  const handleEditFromReview = (targetStep: number) => {
    setEditSnapshot({ ...formData });
    setEditingFromReview(true);
    setCurrentStep(targetStep);
  };

  const handleSaveFromEdit = () => {
    setEditingFromReview(false);
    setEditSnapshot(null);
    setCurrentStep(4);
  };

  const handleBackFromEdit = () => {
    if (editSnapshot) setFormData(editSnapshot);
    setEditingFromReview(false);
    setEditSnapshot(null);
    setCurrentStep(4);
  };

  const handleSubmit = async () => {
    // Read customer info from localStorage — same pattern as requestInterview
    let customer: Record<string, any> = {};
    try {
      const stored = localStorage.getItem("loggedInUser");
      customer = stored ? JSON.parse(stored) : {};
    } catch {
      customer = {};
    }

    // Helper: build { [key]: label, details: description } for a personality answer
    type PersonalityKey = "workingPace" | "collaboration" | "problemType" | "directionLevel" | "communicationStyle";
    const personalityEntry = (key: PersonalityKey) => {
      const value = formData[key] as string;
      if (!value) return null;
      const question = PERSONALITY_QUESTIONS.find((q) => q.key === key);
      const option = question?.options.find((o) => o.value === value);
      return option ? { [key]: option.label, details: option.description } : { [key]: value, details: "" };
    };

    const now = new Date();
    const requestedAt = `${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} at ${now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;

    const payload = {
      requestedAt,

      basics: {
        jobTitle: formData.jobTitle,
        jobPostingUrl: formData.jobPostingUrl || null,
        experienceLevel:
          EXPERIENCE_LEVELS.find((e) => e.value === formData.experienceLevel)?.label ?? formData.experienceLevel,
        idealStartDate: formData.startDate
          ? formData.startDate.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : null,
      },

      capabilities: {
        tools: formData.tools,
        languages: formData.languages,
      },

      personality: (
        ["workingPace", "collaboration", "problemType", "directionLevel", "communicationStyle"] as PersonalityKey[]
      )
        .map(personalityEntry)
        .filter(Boolean),

      additionalInformation: {
        ...(formData.additionalNotes.trim() && { avoidances: formData.additionalNotes.trim() }),
        ...(formData.idealCandidateDescription.trim() && {
          anythingElse: formData.idealCandidateDescription.trim(),
        }),
      },

      customerId: customer.customerId || "",
      customerUserName: customer.name || "",
      customerUserEmail: customer.email || "",
      customerUserId: customer.userId || "",
    };

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitHireRequest(payload);
      analytics.track("Hire Request Submitted", {
        jobTitle: payload.basics.jobTitle,
        experienceLevel: payload.basics.experienceLevel,
      });
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestAnotherHire = () => {
    setFormData(INITIAL_FORM);
    setCurrentStep(0);
    setPhase("intro");
    setSubmitted(false);
    setEditingFromReview(false);
    setEditSnapshot(null);
  };

  const handleExitJourney = () => navigate(-1);

  const stepOnBack = editingFromReview ? handleBackFromEdit : handleBack;
  const stepOnContinue = editingFromReview ? handleSaveFromEdit : handleContinue;

  return (
    <div className="rah-page">
      <Helmet>
        <title>Edge | Request a Hire</title>
      </Helmet>
      <div className="rah-bg-ellipse rah-bg-ellipse--pink" />
      <div className="rah-bg-ellipse rah-bg-ellipse--blue" />

      <Topbar phase={phase} currentStep={currentStep} onBack={handleExitJourney} submitted={submitted} />

      <div className={`rah-content${phase === "intro" || submitted ? " rah-content--intro" : ""}`}>
        {submitted && <SuccessCard onGoBack={() => navigate(-1)} onRequestAnother={handleRequestAnotherHire} />}

        {!submitted && phase === "intro" && <IntroCard onStart={handleStart} onGoBack={handleExitJourney} />}

        {!submitted && phase === "form" && currentStep === 0 && (
          <StepBasics
            data={formData}
            onChange={handleChange}
            onBack={stepOnBack}
            onContinue={stepOnContinue}
            editMode={editingFromReview}
          />
        )}

        {!submitted && phase === "form" && currentStep === 1 && (
          <StepCapabilities
            data={formData}
            onChange={handleChange}
            onBack={stepOnBack}
            onContinue={stepOnContinue}
            editMode={editingFromReview}
            toolOptions={toolOptions}
            languageOptions={LANGUAGES}
          />
        )}

        {!submitted && phase === "form" && currentStep === 2 && (
          <StepPersonality
            data={formData}
            onChange={handleChange}
            onBack={stepOnBack}
            onContinue={stepOnContinue}
            editMode={editingFromReview}
          />
        )}

        {!submitted && phase === "form" && currentStep === 3 && (
          <StepGoodToKnow
            data={formData}
            onChange={handleChange}
            onBack={stepOnBack}
            onContinue={stepOnContinue}
            editMode={editingFromReview}
          />
        )}

        {!submitted && phase === "form" && currentStep === 4 && (
          <StepReview
            data={formData}
            onBack={handleBack}
            onEdit={handleEditFromReview}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        )}
      </div>
    </div>
  );
}

export default RequestAHirePage;
